import { createQueryParams } from '@/utils/query-params';
import { api } from './index';

const BASE_PATH = '/api/internal/charge_closures';

export type ChargeClosureStatus = 'approved' | 'issued' | 'voided';
export type TaxDocumentStatus = 'draft' | 'submitted' | 'accepted' | 'rejected' | 'failed' | 'cancelled';
export type TaxDocumentKind = 'invoice' | 'receipt' | 'credit_note' | 'debit_note' | 'fee_receipt';
/** Why an emission produced no usable DTE, as `Tax::FailureReason` records it.
 *  Null on documents that never failed, and on rows written before the column existed. */
export type TaxDocumentFailureReason = 'credentials' | 'provider_unconfigured' | 'unauthorized'
  | 'not_found' | 'conflict' | 'unavailable' | 'rate_limited' | 'not_created' | 'rejected'
  | 'withdrawn' | 'unknown' | null;

// The live document the closure serializer nests: enough for a list row.
export interface TaxDocumentSummary {
  id: number;
  status: TaxDocumentStatus;
  kind: TaxDocumentKind;
  documentCode: string;
  number: string | null;
  totalCents: number;
  roundingDeltaCents: number;
  failureReason: TaxDocumentFailureReason;
  pdfAvailable: boolean;
  deliveredAt: string | null;
  // Last day the country honours a credit note voiding this document; null when none.
  reversibleUntil: string | null;
}

export interface ChargeClosure {
  id: number;
  patientId: number;
  patientName: string | null;
  representativeId: number | null;
  recipientKind: 'person' | 'organization';
  recipientTaxIdScheme: string;
  recipientTaxId: string;
  recipientName: string | null;
  periodStart: string;
  periodEnd: string;
  status: ChargeClosureStatus;
  currency: string;
  totalAmountCents: number;
  shiftsCount: number;
  description: string | null;
  approvedAt: string | null;
  voidedAt: string | null;
  createdAt: string;
  pdfAvailable: boolean;
  taxDocument: TaxDocumentSummary | null;
}

export interface ChargeClosureLine {
  id: number;
  lineType: 'shift' | 'movement';
  lineDate: string;
  description: string;
  career: string | null;
  dayType: string | null;
  quantity: number;
  currency: string;
  unitPriceCents: number;
  amountCents: number;
}

export interface ClosurePreviewLine {
  lineType: 'shift' | 'movement';
  lineDate: string;
  description: string;
  quantity: number;
  currency: string;
  unitPriceCents: number;
  amountCents: number;
}

// Who the document goes to; null when the patient has no representative.
export interface ClosureRecipient {
  representativeId: number;
  name: string | null;
  // Where the DTE is emailed once the authority accepts it; null when the
  // representative has none on file, in which case nothing is sent.
  email: string | null;
  taxId: string | null;
  taxIdScheme: string;
  kind: 'person' | 'organization';
  valid: boolean;
  taxIdLabel: string;
}

// How the country breaks the closure total down for the tax authority.
export interface ClosureTaxes {
  taxableCents: number;
  taxCents: number;
  exemptCents: number;
  totalCents: number;
  currency: string;
  rate: string;
  taxCategory: string;
  roundingDeltaCents: number;
  documentKind: TaxDocumentKind;
  documentCode: string | null;
  // Whether the account this kind of document is routed to can issue right now
  // (connected, valid credentials, certificate if needed). Which provider that
  // account belongs to is Kuido's business and never leaves the server.
  providerReady: boolean;
}

export interface ClosurePreview {
  lines: ClosurePreviewLine[];
  shiftsCount: number;
  currency: string;
  shiftsTotalCents: number;
  movementsTotalCents: number;
  totalAmountCents: number;
  taxes: ClosureTaxes;
  invoicedShiftsCount: number;
  invoicedTotalCents: number;
  recipient: ClosureRecipient | null;
  // Who the DTE email reaches once the authority accepts it: the patient's
  // representatives chosen for billing emails (the first one while none is).
  mailRecipients: Array<{ fullName: string; email: string }>;
}

export interface ClosurePeriod {
  patientId: number;
  fromDate: string;
  toDate: string;
}

export interface ClosureForm extends ClosurePeriod {
  expectedTotalAmountCents?: number;
  description?: string;
}

export interface ClosureFilters {
  patientId?: number;
  status?: ChargeClosureStatus[];
}

export interface ChargeClosureDetail {
  closure: ChargeClosure;
  lines: ChargeClosureLine[];
}

export const chargeClosuresApi = {
  index(filters: ClosureFilters = {}): Promise<ChargeClosure[]> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?${createQueryParams(filters)}`,
    }).then(response => response.data.chargeClosures);
  },
  show(id: number): Promise<ChargeClosureDetail> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}`,
    }).then(response => ({ closure: response.data.chargeClosure, lines: response.data.meta.lines }));
  },
  // Stateless: nothing is stamped until create.
  preview(period: ClosurePeriod): Promise<ClosurePreview> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/preview?${createQueryParams(period)}`,
    }).then(response => response.data);
  },
  create(form: ClosureForm): Promise<ChargeClosure> {
    return api({
      method: 'post',
      url: BASE_PATH,
      data: form,
    }).then(response => response.data.chargeClosure);
  },
  void(id: number): Promise<ChargeClosure> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/void`,
    }).then(response => response.data.chargeClosure);
  },
  // Redirects to the frozen charges PDF in storage; open it, don't fetch it.
  pdfUrl(id: number): string {
    return `${BASE_PATH}/${id}/pdf`;
  },
};
